import React, { useState, useEffect } from 'react';
import { Button, Modal, TextInput, Spinner, Label, Textarea } from 'flowbite-react';
import OpenAI from 'openai';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const LOCAL_STORAGE_KEY = 'internalLinkingApiKey';
const LOCAL_STORAGE_MODEL = 'internalLinkingModel';
const STORAGE_EXPIRY_KEY = 'internalLinkingStorageExpiry';
const EXPIRY_DURATION_MS = 3 * 60 * 60 * 1000; // 3 hours

function GenerateInternalLinks({ defaultTargetUrl = '', defaultSupportingArticles = [], remainingPosts, setRemainingPosts }) {
  const [keywordsInput, setKeywordsInput] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [apiKey, setApiKey] = useState(process.env.REACT_APP_OPENAI_API_KEY);
  const [model, setModel] = useState('gpt-4.1');
  const [enableWebSearch, setEnableWebSearch] = useState(false);
  const [targetUrl, setTargetUrl] = useState(defaultTargetUrl);
  const [supportingArticles, setSupportingArticles] = useState(defaultSupportingArticles.join('\n'));
  const [primaryLinks, setPrimaryLinks] = useState('');
  const [secondaryLinks, setSecondaryLinks] = useState('');
  const [outputFormatRequest, setOutputFormatRequest] = useState('| Priority | Source Article | Anchor Text | Exact Placement | Updated Paragraph |\n|----------|----------------|-------------|------------------|-------------------|');
  const [instructions, setInstructions] = useState('Please extract real paragraphs or section headings from the supporting articles (do not invent headings). Insert the anchor text naturally into existing content or append a short new paragraph if necessary. Prioritize relevance and natural flow.');
  const [loading, setLoading] = useState(false);
  const [generatedArticle, setGeneratedArticle] = useState('');
  const [query, setQuery] = useState('');
  const [popupKeywordsInput, setPopupKeywordsInput] = useState('');
  const [showQueryForm, setShowQueryForm] = useState(false);

  // Load apiKey and model from localStorage if valid
  // useEffect(() => {
  //   const expiry = localStorage.getItem(STORAGE_EXPIRY_KEY);
  //   const now = Date.now();
  //   if (expiry && now < parseInt(expiry, 10)) {
  //     const storedApiKey = localStorage.getItem(LOCAL_STORAGE_KEY);
  //     const storedModel = localStorage.getItem(LOCAL_STORAGE_MODEL);
  //     if (storedApiKey) setApiKey(storedApiKey);
  //     if (storedModel) setModel(storedModel);
  //   } else {
  //     localStorage.removeItem(LOCAL_STORAGE_KEY);
  //     localStorage.removeItem(LOCAL_STORAGE_MODEL);
  //     localStorage.removeItem(STORAGE_EXPIRY_KEY);
  //   }
  // }, []);

  // useEffect(() => {
  //   const expiry = localStorage.getItem(STORAGE_EXPIRY_KEY);
  //   const now = Date.now();

  //   if (expiry && now < parseInt(expiry, 10)) {
  //     const storedApiKey = localStorage.getItem(LOCAL_STORAGE_KEY);
  //     const storedModel = localStorage.getItem(LOCAL_STORAGE_MODEL);
  //     if (storedApiKey) {
  //       setApiKey(storedApiKey);
  //     } else {
  //       // Check environment variable if localStorage has no API key
  //       const envApiKey = process.env.REACT_APP_OPENAI_API_KEY;
  //       if (envApiKey) setApiKey(envApiKey);
  //     }
  //     if (storedModel) setModel(storedModel);
  //   } else {
  //     localStorage.removeItem(LOCAL_STORAGE_KEY);
  //     localStorage.removeItem(LOCAL_STORAGE_MODEL);
  //     localStorage.removeItem(STORAGE_EXPIRY_KEY);

  //     // Optionally check environment variable after clearing expired keys
  //     const envApiKey = process.env.REACT_APP_OPENAI_API_KEY;
  //     console.log('Api key', process.env.REACT_APP_OPENAI_API_KEY)
  //     if (envApiKey) setApiKey(envApiKey);
  //   }
  // }, []);


  // Save apiKey and model to localStorage with expiry on change
  // useEffect(() => {
  //   if (apiKey && model) {
  //     localStorage.setItem(LOCAL_STORAGE_KEY, apiKey);
  //     localStorage.setItem(LOCAL_STORAGE_MODEL, model);
  //     localStorage.setItem(STORAGE_EXPIRY_KEY, (Date.now() + EXPIRY_DURATION_MS).toString());
  //   }
  // }, [apiKey, model]);

  // Clear localStorage on tab close
  // useEffect(() => {
  //   const handleBeforeUnload = () => {
  //     localStorage.removeItem(LOCAL_STORAGE_KEY);
  //     localStorage.removeItem(LOCAL_STORAGE_MODEL);
  //     localStorage.removeItem(STORAGE_EXPIRY_KEY);
  //   };
  //   window.addEventListener('beforeunload', handleBeforeUnload);
  //   return () => {
  //     window.removeEventListener('beforeunload', handleBeforeUnload);
  //   };
  // }, []);

  useEffect(() => {
    setPopupKeywordsInput(keywordsInput);
  }, [keywordsInput]);

  // Update final query whenever dependent fields change
  useEffect(() => {
    const newQuery = buildFinalQuery();
    setQuery(newQuery);
  }, [targetUrl, popupKeywordsInput, supportingArticles, primaryLinks, secondaryLinks, outputFormatRequest, instructions]);

  const handleKeywordsSubmit = (e) => {
    e.preventDefault();
    setPopupKeywordsInput(keywordsInput);
    setShowModal(true);
    setTargetUrl(defaultTargetUrl);
    setSupportingArticles(defaultSupportingArticles.join('\n'));
    setPrimaryLinks('');
    setSecondaryLinks('');
    setOutputFormatRequest('| Priority | Source Article | Anchor Text | Exact Placement | Updated Paragraph |\n|----------|----------------|-------------|------------------|-------------------|\nnote: please use url in source article field.');
    setInstructions('Please extract real paragraphs or section headings from the supporting articles (do not invent headings). Insert the anchor text naturally into existing content or append a short new paragraph if necessary. Prioritize relevance and natural flow.');
    setGeneratedArticle('');
  };

  const buildFinalQuery = () => {
    let sections = [];

    if (targetUrl.trim()) {
      sections.push(`**Target URL:** ${targetUrl.trim()}`);
    }

    if (popupKeywordsInput.trim()) {
      const keywordsArray = popupKeywordsInput.split('\\n').map(k => k.trim()).filter(k => k.length > 0);
      sections.push(`**Target Keywords:** [${keywordsArray.join(', ')}]`);
    }

    if (supportingArticles.trim()) {
      const articlesList = supportingArticles.split('\\n').map(url => url.trim()).filter(url => url.length > 0).map(url => '- ' + url).join('\\n');
      sections.push(`**Supporting Articles:**  \n${articlesList}`);
    }

    let linkDistribution = [];
    if (primaryLinks !== '' && !isNaN(primaryLinks)) {
      linkDistribution.push(`**Primary Links:** ${primaryLinks}`);
    }
    if (secondaryLinks !== '' && !isNaN(secondaryLinks)) {
      linkDistribution.push(`**Secondary Links:** ${secondaryLinks}`);
    }
    if (linkDistribution.length > 0) {
      sections.push(`# Link Distribution:\n${linkDistribution.join('  \n')}`);
    }

    if (outputFormatRequest.trim()) {
      sections.push(`# Output Format Request:\n${outputFormatRequest.trim()}`);
    }

    if (instructions.trim()) {
      sections.push(`# Instructions:\n${instructions.trim()}`);
    }

    return `# Internal Linking Brief\n${sections.join('\n\n')}\n`;
  };

  // Function to parse markdown tables from AI response and associate link suggestions to remainingPosts
  const parseAndAssociateLinkSuggestions = (responseText) => {
    console.log('Before update:', remainingPosts);
    // Split response by tables (assuming tables start with | Priority | ...)
    const tableRegex = /\| Priority \|[\s\S]*?(?=(\n\n|\n?$))/g;
    const tables = responseText.match(tableRegex) || [];

    // Parse each table into rows
    let linkSuggestionsMap = {};

    tables.forEach((table) => {
      const lines = table.split('\n').filter(line => line.trim().startsWith('|'));
      // Skip header and separator lines
      const dataLines = lines.slice(2);

      dataLines.forEach((line) => {
        const cols = line.split('|').map(col => col.trim()).filter(col => col.length > 0);
        if (cols.length >= 5) {
          const priority = cols[0];
          const sourceArticle = cols[1];
          const anchorText = cols[2];
          const exactPlacement = cols[3];
          const updatedParagraph = cols[4];

          if (!linkSuggestionsMap[sourceArticle]) {
            linkSuggestionsMap[sourceArticle] = [];
          }
          linkSuggestionsMap[sourceArticle].push({
            priority,
            anchorText,
            exactPlacement,
            updatedParagraph,
          });
        }
      });
    });

    // Update remainingPosts with linkSuggestions
    const updatedPosts = remainingPosts.map(post => {
      const suggestions = linkSuggestionsMap[post.url] || [];
      return { ...post, linkSuggestions: suggestions };
    });

    console.log('After update:', updatedPosts)

    setRemainingPosts(updatedPosts);
  };

  const handleGenerate = async () => {
    if (!apiKey.trim() || !model.trim() || !query.trim()) {
      alert('API Key, Model, and Final Query fields are mandatory.');
      return;
    }
    setLoading(true);
    setGeneratedArticle('');
    try {
      const openai = new OpenAI({
        apiKey: apiKey,
        dangerouslyAllowBrowser: true,
      });
      const response = await openai.responses.create({
        model: model,
        input: query,
        ...(enableWebSearch ? { tools: [{ type: "web_search_preview" }] } : {})
      });
      const outputText = response.output_text || 'No output from AI.';
      setGeneratedArticle(outputText);
      parseAndAssociateLinkSuggestions(outputText);
    } catch (error) {
      setGeneratedArticle('Error generating article: ' + error.message);
    } finally {
      setLoading(false);
      setShowModal(false);
    }
  };

  return (
    <div>
      <form onSubmit={handleKeywordsSubmit} className="mb-6">
        <Label htmlFor="keywords" className="block mb-2 font-semibold">
          Target Keywords (one per line)
        </Label>
        <textarea
          id="keywords"
          rows={4}
          value={keywordsInput}
          onChange={(e) => setKeywordsInput(e.target.value)}
          className="w-full border border-gray-300 rounded p-2 resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder="Enter target keywords, one per line"
        />
        <div className="text-right text-sm text-gray-500 mb-2">
          {keywordsInput.split('\n').filter(k => k.trim().length > 0).length} keywords
        </div>
        <Button type="submit" size="sm" className="mt-2">
          Submit
        </Button>
      </form>
      {loading && (
        <div className="mb-4 flex items-center gap-2">
          <Spinner size="sm" />
          <span>Generating internal linking suggestions...</span>
        </div>
      )}
      {generatedArticle && (
        <div className="mb-4 p-4 border rounded prose max-w-full whitespace-pre-wrap">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{generatedArticle}</ReactMarkdown>
        </div>
      )}

      <Modal show={showModal} onClose={() => { setShowModal(false); setShowQueryForm(false) }} size={showQueryForm ? "4xl" : "lg"} popup>
        <Modal.Header />
        <Modal.Body>
          <div className="space-y-4">
            <div className='bg-gray-200 py-2 px-3 rounded-lg font-medium cursor-pointer hover:bg-gray-300' onClick={() => setShowQueryForm((curr) => !curr)}>
              {showQueryForm ? "Hide" : "Show"} Query Form
            </div>
            {showQueryForm &&
              <div>
                <div>
                  <Label htmlFor="apiKey" className="block mb-1 font-semibold mt-3">
                    OpenAI API Key *
                  </Label>
                  <TextInput
                    id="apiKey"
                    type="password"
                    value={apiKey}
                    onChange={(e) => setApiKey(e.target.value)}
                    placeholder="Enter your OpenAI API key"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="model" className="block mb-1 font-semibold mt-3">
                    Model * (gpt-4.1, gpt-4.1-mini, gpt-5, gpt-5-mini)
                  </Label>
                  <TextInput
                    id="model"
                    value={model}
                    onChange={(e) => setModel(e.target.value)}
                    placeholder="e.g. gpt-4.1"
                    required
                  />
                </div>
                <div className="flex items-center gap-2 mt-3">
                  <input
                    id="enableWebSearch"
                    type="checkbox"
                    checked={enableWebSearch}
                    onChange={(e) => setEnableWebSearch(e.target.checked)}
                    className="w-4 h-4"
                  />
                  <Label htmlFor="enableWebSearch" className="font-semibold">
                    Enable Web Search
                  </Label>
                </div>
                <div>
                  <Label htmlFor="targetUrl" className="block mb-1 font-semibold mt-3">
                    Target URL
                  </Label>
                  <TextInput
                    id="targetUrl"
                    value={targetUrl}
                    onChange={(e) => setTargetUrl(e.target.value)}
                    placeholder="Enter target URL"
                  />
                </div>
                <div>
                  <Label htmlFor="popupKeywordsInput" className="block mb-1 font-semibold mt-3">
                    Target Keywords
                  </Label>
                  <textarea
                    id="popupKeywordsInput"
                    rows={4}
                    value={popupKeywordsInput}
                    onChange={(e) => setPopupKeywordsInput(e.target.value)}
                    className="w-full border border-gray-300 rounded p-2 resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter target keywords, one per line"
                  />
                  <div className="text-right text-sm text-gray-500 mb-2">
                    {popupKeywordsInput.split('\n').filter(k => k.trim().length > 0).length} keywords
                  </div>
                </div>
                <div>
                  <Label htmlFor="supportingArticles" className="block mb-1 font-semibold mt-3">
                    Supporting Articles (one URL per line)
                  </Label>
                  <textarea
                    id="supportingArticles"
                    rows={4}
                    value={supportingArticles}
                    onChange={(e) => setSupportingArticles(e.target.value)}
                    className="w-full border border-gray-300 rounded p-2 resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter supporting article URLs, one per line"
                  />
                  <div className="text-right text-sm text-gray-500 mb-2">
                    {supportingArticles.split('\n').filter(url => url.trim().length > 0).length} articles
                  </div>
                </div>
                <div>
                  <Label htmlFor="primaryLinks" className="block mb-1 font-semibold mt-3">
                    Primary Links
                  </Label>
                  <TextInput
                    id="primaryLinks"
                    type="number"
                    min={0}
                    value={primaryLinks}
                    onChange={(e) => setPrimaryLinks(e.target.value === '' ? '' : Math.max(0, Number(e.target.value)))}
                    placeholder="Enter number of primary links"
                  />
                </div>
                <div>
                  <Label htmlFor="secondaryLinks" className="block mb-1 font-semibold mt-3">
                    Secondary Links
                  </Label>
                  <TextInput
                    id="secondaryLinks"
                    type="number"
                    min={0}
                    value={secondaryLinks}
                    onChange={(e) => setSecondaryLinks(e.target.value === '' ? '' : Math.max(0, Number(e.target.value)))}
                    placeholder="Enter number of secondary links"
                  />
                </div>
                <div>
                  <Label htmlFor="outputFormatRequest" className="block mb-1 font-semibold mt-3">
                    Output Format Request
                  </Label>
                  <Textarea
                    id="outputFormatRequest"
                    rows={4}
                    value={outputFormatRequest}
                    onChange={(e) => setOutputFormatRequest(e.target.value)}
                    placeholder="Enter output format request"
                  />
                </div>
                <div>
                  <Label htmlFor="instructions" className="block mb-1 font-semibold mt-3">
                    Instructions
                  </Label>
                  <Textarea
                    id="instructions"
                    rows={4}
                    value={instructions}
                    onChange={(e) => setInstructions(e.target.value)}
                    placeholder="Enter instructions"
                  />
                </div>
                <div>
                  <Label htmlFor="finalQuery" className="block mb-1 font-semibold mt-3">
                    Final Query *
                  </Label>
                  <Textarea
                    id="finalQuery"
                    rows={8}
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Final query to send to AI"
                    required
                  />
                </div>
              </div>
            }
            <div className='flex justify-center'>
              <Button onClick={handleGenerate} disabled={loading}>
                {loading ? 'Generating...' : 'Generate'}
              </Button>
            </div>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
}

export default GenerateInternalLinks;
